import BasicModal from './BasicModal';
import PremiumIconCrown from '../PremiumIconCrown';
import ExportUpsellCard from '../ExportUpsellCard';
import { MagnifyingGlassIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useRef, useContext, useState, useEffect, useMemo } from 'react';
import { useUser } from '@clerk/react';
import { DiffitUserContext } from '../../contexts/DiffitUserContext';
import { isPremiumUser, GET_PREMIUM_ACCESS_URL } from '../../utils/isPremiumUser';
import PremiumOfferCta from '../PremiumOfferCta';
import { usePremiumOffer } from '../../contexts/premiumOffer';
import { useExportGate } from '../../utils/exportTrialState';
import LoadingSpinner from '../LoadingSpinner';
import generateOptions from '../../assets/generate-options.json';
import { Mixpanel } from '../../services/Mixpanel';
import { Listbox } from '../Listbox';
import { StandardPill } from '../StandardPill';
import { UpgradeMessage } from '../UpgradeMessage';
import { useSkillsSelection } from '../../hooks/useSkillsSelection';
import {
  SUBJECT_MAP,
  STATE_STANDARD_SETS,
  STANDARD_SET_MAP,
  SUBJECT_FOR_QUESTIONS_OPTIONS,
  GRADE_LEVEL_MAP,
  isComprehensionSkill,
  buildSubjectForQuestionsOptions,
  getSubjectCourses,
  isGradeAgnosticSubject,
} from '../../utils/skills';

// Helper to get subject options for questions dropdown using centralized util
const getSubjectForQuestionOptions = (isStateSetSelected, questionType) =>
  buildSubjectForQuestionsOptions(isStateSetSelected, questionType);

// bts-2026-freemium: the standards-gate overlay fades in over the lower half of the
// skills list — transparent at the top so the first rows read as a live teaser, opaque
// below so it clearly locks. Same fade idea as the My Resources GatedResourcesPreview.
const GATE_FADE_MASK = 'linear-gradient(to bottom, transparent 0%, black 30%)';

/**
 * This modal operates in two modes:
 * 1. 'packetSkills' - For selecting skills/standards to align content
 * 2. 'addQuestion' - For generating questions based on selected criteria
 */
export default function AddSkillsModal({
  showModal,
  setShowModal,
  onSubmit,
  selectedLanguageStandards: initialLanguageStandards = [],
  selectedContentStandard: initialContentStandard = null, // Supports both single object (legacy) and array
  modalType = 'packetSkills',
  questionType, // The actual question type: mcq, saq, oeq
  initialSubjectKey = null, // Pre-selected subject key for the modal
  maxSelectableOverride = null, // Override max selectable standards (null = use default)
}) {
  const initialFocusRef = useRef();
  const focusInputRefs = useRef({});
  const { diffitUser } = useContext(DiffitUserContext);
  const { user } = useUser();
  const { isOffered } = usePremiumOffer();

  // bts-2026-freemium: aligning to a specific standard is premium. Free users may still
  // open the modal and browse (comprehension stays selectable); the skills list fades
  // under a "Get Access" overlay covering its lower half — mirroring the My Resources
  // gate (GatedResourcesPreview). Trial users get a countdown card and full access.
  // gateActive (flag on + LD identified) is required; flag-off renders exactly as before.
  const hasPremium = isPremiumUser(user, diffitUser);
  const { tier, gateActive, showTrialCountdown } = useExportGate();
  const standardsGated = modalType === 'packetSkills' && gateActive && tier === 'free';

  // Local state for customInstructions and numberToGenerate
  const [customInstructions, setCustomInstructions] = useState('');
  const [numberToGenerate, setNumberToGenerate] = useState(generateOptions['number'][0]);

  // Use the new hook for all state management
  const {
    filters,
    selectedLanguageStandards,
    selectedContentStandards,
    skills: filteredSkills,
    isLoading: loadingSkills,
    isContentStandard,
    effectiveSubjectKey,
    isStateSetSelected,
    maxSelectable,
    actions,
    COMPREHENSION_SKILL,
  } = useSkillsSelection(
    modalType,
    { questionType, subjectKey: initialSubjectKey, maxSelectable: maxSelectableOverride },
    initialLanguageStandards,
    initialContentStandard
  );

  const {
    subjectKey: selectedSubjectKey,
    gradeKey,
    courseKey,
    standardSetKey,
    subjectForQuestionsKey,
    searchQuery,
  } = filters;

  // Course-based subjects (AP) swap the state dropdown for a course dropdown, and drop the
  // grade dropdown: their standards are not grade-specific.
  const subjectCourses = getSubjectCourses(effectiveSubjectKey);
  const hideGradeSelection = isGradeAgnosticSubject(effectiveSubjectKey);

  // Subject options for questions
  const subjectForQuestionOptions = useMemo(
    () => getSubjectForQuestionOptions(isStateSetSelected, questionType),
    [isStateSetSelected, questionType]
  );

  // UI state computations
  const showStandardsFilterBar =
    modalType === 'packetSkills' ||
    (modalType === 'addQuestion' && subjectForQuestionsKey !== 'CUSTOM');
  const isCustomQuestionType = modalType === 'addQuestion' && subjectForQuestionsKey === 'CUSTOM';

  const title =
    modalType === 'packetSkills'
      ? isContentStandard
        ? 'Align to a Standard'
        : 'Align to Skills or Standards'
      : 'Add Questions';

  const buttonText =
    modalType === 'packetSkills'
      ? isContentStandard
        ? 'Set Standard'
        : 'Set Skills/Standards'
      : 'Get Questions';

  const instructions =
    modalType === 'packetSkills'
      ? isContentStandard
        ? 'Choose a standard below to generate an aligned resource. If you are using your own text, make sure it is related to the standard!'
        : 'Choose up to three skills or standards below. Diffit will generate a resource for those standards based on your topic or text.'
      : null;

  /**
   * Handles form submission with analytics tracking
   * Formats selected skills based on modal type and max selection
   */
  const submitForm = () => {
    // Combine skills for analytics and submission
    const allSkills = isContentStandard ? selectedContentStandards : selectedLanguageStandards;

    // Track analytics events
    if (modalType === 'packetSkills') {
      Mixpanel.track('Submit Add Standards Modal', {
        subject: selectedSubjectKey,
        skills: allSkills.map((skill) => skill.id),
      });
    } else if (modalType === 'addQuestion') {
      const isPremiumFeature = allSkills.some((skill) => !isComprehensionSkill(skill));

      Mixpanel.track('Generate More Options', {
        resourceType: 'questions',
        selections: {
          number: numberToGenerate.id,
          skills: allSkills.map((skill) => skill.id),
          ...(isCustomQuestionType && { customInstructions }),
        },
        isPremiumFeature,
      });
    }

    // Format selected skills based on mode
    const selected = isContentStandard
      ? maxSelectable > 1
        ? selectedContentStandards
        : selectedContentStandards[0]
      : maxSelectable > 1
        ? selectedLanguageStandards
        : modalType === 'addQuestion'
          ? (selectedLanguageStandards[0] ?? COMPREHENSION_SKILL)
          : selectedLanguageStandards[0];

    onSubmit({
      selected,
      numberToGenerate,
      ...(isCustomQuestionType && { customInstructions }),
    });
    setShowModal(false);
  };

  // Track modal open events
  useEffect(() => {
    if (showModal && modalType === 'packetSkills') {
      Mixpanel.track('Open Add Standards Modal', { subject: selectedSubjectKey });
    }
  }, [showModal, modalType, selectedSubjectKey]);

  // Empty state checks
  const needsToChooseSubjectForQuestions = modalType === 'addQuestion' && !subjectForQuestionsKey;
  const showEmptyState =
    !loadingSkills &&
    !needsToChooseSubjectForQuestions &&
    filteredSkills.length === 0 &&
    !isCustomQuestionType;
  const showNoStandardsPlaceholder = showEmptyState && isStateSetSelected && !searchQuery;

  // Packet mode requires state selection unless subject has an override
  const requireStateForPacket =
    modalType === 'packetSkills' &&
    !SUBJECT_MAP[effectiveSubjectKey]?.setOverride &&
    !isStateSetSelected;

  // Check if standards have changed from initial state
  const hasChanges = useMemo(() => {
    // Check language standards
    const initialLanguageIds = (initialLanguageStandards || []).map((s) => s?.id).filter(Boolean);
    const currentLanguageIds = (selectedLanguageStandards || []).map((s) => s?.id).filter(Boolean);

    if (initialLanguageIds.length !== currentLanguageIds.length) return true;
    if (!initialLanguageIds.every((id) => currentLanguageIds.includes(id))) return true;

    // Check content standards (support both single object and array)
    const initialContentArray = Array.isArray(initialContentStandard)
      ? initialContentStandard
      : initialContentStandard
        ? [initialContentStandard]
        : [];
    const initialContentIds = initialContentArray.map((s) => s?.id).filter(Boolean);
    const currentContentIds = (selectedContentStandards || []).map((s) => s?.id).filter(Boolean);

    if (initialContentIds.length !== currentContentIds.length) return true;
    if (!initialContentIds.every((id) => currentContentIds.includes(id))) return true;

    return false;
  }, [
    initialLanguageStandards,
    initialContentStandard,
    selectedLanguageStandards,
    selectedContentStandards,
  ]);
  const isSaveButtonDisabled =
    loadingSkills || (modalType === 'packetSkills' && !hasChanges) || requireStateForPacket;

  return (
    <BasicModal
      showModal={showModal}
      setShowModal={setShowModal}
      initialFocusRef={initialFocusRef}
      dialogPadding={''}
      maxWidth={'sm:max-w-3xl'}
      iconBackground={'bg-none'}>
      <div className='pt-8 px-8 h-full'>
        <div className='flex flex-row items-baseline sm:max-w-3xl'>
          <h1 className='text-xl sm:text-2xl font-semibold text-gray-800 mr-2'>{title}</h1>
          {/* Title crown: shown for everyone flag-off (byte-identical to today); flag-on it
              follows the family rule — free/trial keep it, paid drops it. */}
          {modalType === 'packetSkills' && (!gateActive || tier !== 'paid') && (
            <PremiumIconCrown className='h-5 w-5' wrapperClassName='translate-y-[6px]' />
          )}
        </div>

        {/* flag-off keeps the legacy flat upsell; flag-on uses the overlay (free) below
            and the trial countdown card under the instructions, right above the dropdowns. */}
        {modalType === 'packetSkills' && !hasPremium && !gateActive && <UpgradeMessage />}

        {instructions && (
          <span className='mt-1 text-gray-800 text-xs sm:text-sm hidden sm:block'>
            {instructions}
          </span>
        )}
        {modalType === 'packetSkills' && showTrialCountdown && (
          <div className='mt-3'>
            <ExportUpsellCard
              mode='trial'
              location='align_modal'
              trackEvent={(name, props) => Mixpanel.track(name, props)}
            />
          </div>
        )}
        {/* Subject for Questions Selection (Add Questions Mode) */}
        {modalType === 'addQuestion' && (
          <div className='w-full sm:flex-grow mt-2 mb-2'>
            <Listbox
              selected={{
                name: SUBJECT_FOR_QUESTIONS_OPTIONS[subjectForQuestionsKey]?.name,
                value: subjectForQuestionsKey,
              }}
              setSelected={(selection) => {
                // Listbox may pass either the value directly or the full option object
                const key = typeof selection === 'string' ? selection : selection?.value;
                actions.setSubjectForQuestions(key);
              }}
              options={subjectForQuestionOptions}
              label={'Generate questions based on...'}
              placeholder={'Choose a subject for questions'}
            />
            {!hasPremium && subjectForQuestionsKey !== 'DOK' && <UpgradeMessage />}
          </div>
        )}
        {isCustomQuestionType && modalType === 'addQuestion' && (
          <div className='w-full mt-2' style={{ height: '400px' }}>
            <label
              htmlFor='custom-instructions'
              className='block text-sm font-medium leading-6 text-gray-900'>
              Custom Instructions
            </label>
            <div className='mt-1'>
              <textarea
                rows={3}
                disabled={!hasPremium}
                name='custom-instructions'
                id='custom-instructions'
                className='block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-emerald-500 sm:text-sm sm:leading-6'
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
                placeholder='Enter your custom instructions for the question...'
              />
            </div>
          </div>
        )}
        {/* Standards Filter Bar */}
        {showStandardsFilterBar && (
          <div
            id='filter-skills-row'
            className='mt-3 flex flex-col justify-between flex-wrap sm:flex-nowrap content-center'>
            <div className='flex flex-col sm:flex-row gap-2 w-full'>
              {/* State Selection Dropdown - Only show if no set override */}
              {!SUBJECT_MAP[effectiveSubjectKey]?.setOverride && (
                <div className='w-full sm:flex-grow'>
                  <Listbox
                    selected={{
                      name: STATE_STANDARD_SETS[standardSetKey]?.name || 'Select State',
                      value: standardSetKey,
                    }}
                    setSelected={(key) => actions.setStandardSet(key)}
                    options={Object.entries(STATE_STANDARD_SETS).map(([key, { name }]) => ({
                      name,
                      value: key,
                    }))}
                    label={false}
                    placeholder='Select State'
                  />
                </div>
              )}
              {/* Course Dropdown - for subjects split into courses (AP) */}
              {subjectCourses && (
                <div className='w-full sm:flex-grow'>
                  <Listbox
                    selected={{
                      name: subjectCourses[courseKey]?.name || 'Select Course',
                      value: courseKey,
                    }}
                    setSelected={(key) => actions.setCourse(key)}
                    options={Object.entries(subjectCourses).map(([key, { name }]) => ({
                      name,
                      value: key,
                    }))}
                    label={false}
                    placeholder='Select Course'
                  />
                </div>
              )}
              {/* Grade Level Dropdown */}
              {!hideGradeSelection && (
                <div className='w-full sm:flex-grow'>
                  <Listbox
                    selected={GRADE_LEVEL_MAP[gradeKey]}
                    setSelected={(gradeObj) => {
                      const key = Object.keys(GRADE_LEVEL_MAP).find(
                        (k) => GRADE_LEVEL_MAP[k] === gradeObj
                      );
                      if (key) {
                        actions.setGrade(key);
                      }
                    }}
                    options={Object.entries(GRADE_LEVEL_MAP).map(([, gradeObj]) => ({
                      name: gradeObj.name,
                      value: gradeObj,
                    }))}
                    label={false}
                  />
                </div>
              )}
              {/* Search Input */}
              <div className='w-full sm:flex-grow'>
                <div className='relative rounded-md w-full'>
                  <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                    <MagnifyingGlassIcon className='h-5 w-5 text-gray-400' aria-hidden='true' />
                  </div>
                  <input
                    type='text'
                    name='Search'
                    id='search'
                    value={searchQuery}
                    onChange={(e) => actions.setSearchQuery(e.target.value)}
                    className='block w-full rounded-md border-0 py-2 pl-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-emerald-500 sm:text-sm sm:leading-6'
                    placeholder='Search...'
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Skills Container */}
        <div className='relative mt-3'>
          <div
            className={`overflow-y-scroll ${
              standardsGated
                ? 'h-[min(360px,50vh)] sm:h-[min(460px,50vh)]'
                : 'h-[min(400px,50vh)] sm:h-[min(500px,50vh)]'
            } flex flex-col gap-2 justify-start ${isCustomQuestionType ? 'hidden' : ''}`}>
            {/* Loading State */}
            {loadingSkills && (
              <div className='h-full w-full flex justify-center'>
                <LoadingSpinner size='6' className='self-center self-justify-center' />
              </div>
            )}
            {/* Empty States — suppressed while the free gate is active so the "Get Access"
              card is the sole content (no half-covered "select a state" placeholder). */}
            {!standardsGated &&
              (!loadingSkills && showNoStandardsPlaceholder ? (
                <div className='flex flex-grow items-center justify-center'>
                  <div className='text-gray-600 text-center'>
                    <div className='sm:text-xl font-semibold'>
                      {hideGradeSelection
                        ? `Sorry, we do not have ${subjectCourses?.[courseKey]?.name || SUBJECT_MAP[selectedSubjectKey]?.name} standards`
                        : `Sorry, we do not have ${GRADE_LEVEL_MAP[gradeKey]?.name?.replace('Grade', 'grade')} ${SUBJECT_MAP[selectedSubjectKey]?.name} for ${
                            STANDARD_SET_MAP[standardSetKey]?.name || 'your selection'
                          }`}
                    </div>
                    <div className='text-xs sm:text-normal'>
                      Don't see your standards?{' '}
                      <a
                        href='https://forms.gle/ex65mCibj4BBo8px8'
                        target='_blank'
                        className='text-emerald-500 hover:text-emerald-600'>
                        Request more standards
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                showEmptyState && (
                  <div className='flex flex-grow items-center justify-center'>
                    <div className='text-gray-600 text-center'>
                      <div className='sm:text-xl font-semibold'>
                        {searchQuery
                          ? 'No standards found matching your search'
                          : 'Select a state to see standards here'}
                      </div>
                      <div className='text-xs sm:text-normal'>
                        Don't see your standards?{' '}
                        <a
                          href='https://forms.gle/ex65mCibj4BBo8px8'
                          target='_blank'
                          className='text-emerald-500 hover:text-emerald-600'>
                          Request more standards
                        </a>
                      </div>
                    </div>
                  </div>
                )
              ))}

            <ScrollableListOfSkills
              skills={
                requireStateForPacket || needsToChooseSubjectForQuestions || isCustomQuestionType
                  ? []
                  : filteredSkills
              }
              selectedLanguageStandards={selectedLanguageStandards}
              selectedContentStandards={selectedContentStandards}
              setSelectedSkills={actions.setSelectedSkills}
              maxSelectable={maxSelectable}
              hasPremium={hasPremium}
              showPremiumIcon={modalType !== 'packetSkills' && effectiveSubjectKey === 'DOK'}
              isContentMode={isContentStandard}
              setFocusText={actions.setFocusText}
              focusInputRefs={focusInputRefs}
            />
          </div>
          {standardsGated && !isCustomQuestionType && (
            <div
              style={{ maskImage: GATE_FADE_MASK, WebkitMaskImage: GATE_FADE_MASK }}
              className='absolute inset-x-0 bottom-0 h-3/5 flex flex-col justify-center bg-white backdrop-blur-[3px] pointer-events-none'>
              <div className='px-4 pointer-events-auto translate-y-6'>
                {isOffered ? (
                  // Not a button: the offer and its plans link are two targets, and nesting
                  // them inside one would make the click destination unpredictable.
                  <div className='mx-auto flex max-w-2xl items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-6 py-4 text-left shadow-lg'>
                    <PremiumOfferCta
                      variant='inline'
                      question='Need to align to specific standards?'
                      location='align_modal'
                    />
                  </div>
                ) : (
                  <button
                    type='button'
                    onClick={() => {
                      Mixpanel.track('Get Premium Access Clicked', { location: 'align_modal' });
                      window.open(GET_PREMIUM_ACCESS_URL, '_blank');
                    }}
                    className='mx-auto flex max-w-2xl items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-6 py-4 text-left shadow-lg hover:bg-gray-100'>
                    <PremiumIconCrown
                      className='h-6 w-6'
                      wrapperClassName='flex-shrink-0'
                      toolTipProps={{ text: undefined }}
                    />
                    <span className='flex flex-col text-xs text-gray-900'>
                      <span>Need to align to specific standards?</span>
                      <span className='mt-0.5 inline-flex items-center font-medium text-amber-700'>
                        See Plans
                        <ChevronRightIcon className='ml-0.5 h-3 w-3' />
                      </span>
                    </span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Modal Footer — hidden while the free standards gate is active (nothing selectable) */}
      {!standardsGated && (
        <div
          id='modal-footer'
          className='bg-gray-100 h-26 p-2 sm:p-0 sm:h-16 w-full sticky bottom-0'>
          <div className='flex content-center h-full w-full basis-1 flex-wrap justify-start'>
            {/* Selected Skills Pills */}
            <div
              className={`${
                maxSelectable !== 1 ? 'self-baseline sm:self-center' : 'self-center'
              } flex flex-row flex-wrap gap-2 sm:ml-8 flex-1 justify-start`}>
              {/* Show pills based on current mode */}
              {isContentStandard
                ? selectedContentStandards.map((skill) => (
                    <StandardPill
                      key={skill?.id}
                      standard={skill}
                      onClear={() => {
                        actions.setSelectedContentStandards(
                          selectedContentStandards.filter((el) => el.id !== skill.id)
                        );
                      }}
                    />
                  ))
                : selectedLanguageStandards.map((skill) => (
                    <StandardPill
                      key={skill?.id}
                      standard={skill}
                      onClear={() => {
                        actions.setSelectedLanguageStandards(
                          selectedLanguageStandards.filter((el) => el.id !== skill.id)
                        );
                      }}
                    />
                  ))}
            </div>
            {/* Action Buttons */}
            <div className='ml-2 self-center flex flex-row mr-2 justify-start'>
              {/* Question Count Selector */}
              {modalType !== 'packetSkills' && (
                <div className='flex flex-row gap-2 self-center justify-center align-center font-semibold text-[14px] mr-2'>
                  <p className='self-center justify-center text-xs sm:text-[14px] whitespace-nowrap'>
                    How many questions?
                  </p>
                  <Listbox
                    selected={{
                      name: numberToGenerate.id,
                      value: numberToGenerate,
                    }}
                    setSelected={setNumberToGenerate}
                    options={generateOptions['number'].map((item) => ({
                      name: item.id,
                      value: item,
                    }))}
                    label={false}
                    openDirection={'up'}
                    icon='UpDownChevronIcon'
                  />
                </div>
              )}
              {/* Submit Button */}
              <div className='flex-1 flex justify-end'>
                <button
                  ref={initialFocusRef}
                  type='button'
                  className={`self-center whitespace-nowrap inline-flex justify-center px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-80 disabled:cursor-not-allowed ${
                    isSaveButtonDisabled
                      ? 'bg-gray-200 text-gray-900'
                      : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  }`}
                  onClick={submitForm}
                  disabled={isSaveButtonDisabled}>
                  {buttonText}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </BasicModal>
  );
}

/**
 * ScrollableListOfSkills - Renders a list of selectable skills/standards
 */
const ScrollableListOfSkills = ({
  skills,
  selectedLanguageStandards,
  selectedContentStandards,
  setSelectedSkills,
  maxSelectable = 1,
  hasPremium,
  showPremiumIcon,
  isContentMode,
  setFocusText,
  focusInputRefs,
}) => {
  if (!skills || skills.length === 0) return null;

  // Get all selected skills based on mode
  const allSelectedSkills = isContentMode
    ? selectedContentStandards || []
    : selectedLanguageStandards || [];

  const safeAllSelectedSkills = allSelectedSkills.filter((s) => s && s.id);

  /**
   * Determines if a skill is disabled for selection
   * Free users can only select the comprehension skill
   */
  const isDisabled = (skill) => !hasPremium && !isComprehensionSkill(skill);

  /**
   * Handles skill selection/deselection
   */
  const handleSkillClick = (skill) => {
    if (isDisabled(skill)) return;

    const isSelected = safeAllSelectedSkills.some((s) => s.id === skill.id);

    if (isSelected) {
      // Deselect the skill
      setSelectedSkills(safeAllSelectedSkills.filter((s) => s.id !== skill.id));
    } else {
      // Ensure the skill gets a focus property when selected (only for language standards)
      const skillWithFocus = isContentMode ? skill : { ...skill, focus: skill.focus || '' };

      // Check if we can add more skills
      if (maxSelectable === 1) {
        // Single selection mode (content standard)
        setSelectedSkills([skillWithFocus]);
      } else if (safeAllSelectedSkills.length < maxSelectable) {
        // Multiple selection mode (language standards)
        setSelectedSkills([...safeAllSelectedSkills, skillWithFocus]);
      }
      // Auto-focus on the focus input for the newly selected skill (only for language standards)
      if (!isContentMode) {
        setTimeout(() => {
          const inputRef = focusInputRefs.current[skill.id];
          if (inputRef) {
            inputRef.focus();
          }
        }, 100);
      }
    }
  };

  return (
    <>
      {skills.map((skill, index) => {
        const isSelected = safeAllSelectedSkills.some((s) => s.id === skill.id);

        // Check if we're at limit
        const isAtLimit =
          maxSelectable > 1 && safeAllSelectedSkills.length >= maxSelectable && !isSelected;

        const shouldDisableText = isDisabled(skill) || isAtLimit;

        return (
          <div
            key={`${skill.id}_${skill.grade_level}_${index}`}
            className='border-b border-gray-200'>
            <div
              role='button'
              disabled={isDisabled(skill)}
              className={`flex flex-row justify-between py-2 max-w-full ${
                isDisabled(skill) ? 'pointer-events-none' : 'cursor-pointer'
              }`}
              onClick={() => handleSkillClick(skill)}>
              {/* Skill Content */}
              <div className='flex flex-row flex-grow min-w-0'>
                <div
                  className={`${
                    shouldDisableText ? 'text-gray-500' : ''
                  } flex flex-col gap-1 ml-2 max-w-full`}>
                  {/* Skill Header */}
                  <span className='flex flex-row items-center'>
                    <StandardPill
                      key={skill.id}
                      disabled={isDisabled(skill) || isAtLimit}
                      standard={skill}
                      showToolTip={false}
                    />
                    {showPremiumIcon && !isComprehensionSkill(skill) && (
                      <PremiumIconCrown
                        className='h-3 w-3 ml-1'
                        toolTipProps={{ text: undefined }}
                      />
                    )}
                    <span className='ml-2 text-gray-500 text-xs font-normal overflow-hidden text-ellipsis whitespace-wrap sm:whitespace-nowrap'>
                      {skill.strand}
                    </span>
                  </span>
                  {/* Skill Description */}
                  <span
                    className={`${
                      shouldDisableText ? 'text-gray-500' : 'text-gray-700'
                    } ml-1 text-xs sm:text-sm`}>
                    {skill.description}
                  </span>
                </div>
              </div>
              {/* Selection Control */}
              <div className='flex flex-row items-center'>
                <input
                  type={maxSelectable !== 1 ? 'checkbox' : 'radio'}
                  id={skill.name}
                  name={skill.name}
                  checked={isSelected}
                  onMouseDown={(event) => event.stopPropagation()}
                  onMouseUp={(event) => event.stopPropagation()}
                  onChange={(event) => event.stopPropagation()}
                  disabled={isAtLimit}
                  className={`${isAtLimit ? 'bg-gray-300' : ''} ${
                    maxSelectable !== 1 ? 'h-5 w-5 rounded' : ''
                  } ml-6 mr-5 border-gray-300 text-emerald-600 focus:ring-emerald-600`}
                />
              </div>
            </div>
            {/* Focus Input - Show when skill is selected (only for language standards) */}
            {isSelected && !isContentMode && (
              <div className='px-2 pb-2'>
                <input
                  ref={(ref) => {
                    if (ref) {
                      focusInputRefs.current[skill.id] = ref;
                    }
                  }}
                  type='text'
                  placeholder='Optional: provide additional detail about the specific skill(s) to focus on'
                  value={allSelectedSkills.find((s) => s.id === skill.id)?.focus || ''}
                  onChange={(e) => setFocusText(skill.id, e.target.value)}
                  className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500'
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};
